/**
 * _display_format.js — draw a money amount or a date the way Jinja does.
 *
 * A value typed offline shows in its slot before the server has seen it, so it
 * must read exactly as the page will once the edit syncs. The rules are the
 * server's: the page carries the business's region settings on
 * <body data-display-format> (application/utils/display_format.py, the dict the
 * format_money and region_date filters read), and these functions only apply
 * them. The device's own locale is never consulted.
 *
 * formatMoney mirrors format_money: round to the places (half to even, as
 * Python's Decimal.quantize does), group the whole part, then join with the
 * region's separators and put the symbol before or after, with the region's
 * symbol_separator between symbol and number. Intl is used only
 * for the rounding and grouping, on a fixed locale, never for what the
 * separators or symbol are.
 *
 * tests/test_display_format.py runs these in node against the Jinja filters for
 * AU, GB, IE, DE, FR and NL businesses.
 */

/** The page's display format, or null on a page that carries none. */
export function pageDisplayFormat() {
    try {
        return JSON.parse(document.body.dataset.displayFormat || "null");
    } catch (_e) {
        return null;
    }
}

/**
 * A submitted amount ("1234.5") as format_money renders it. ``places``
 * overrides the region's decimal places, as format_money(places=0) does.
 * Text that is not a number comes back unchanged, as the filter returns it.
 */
export function formatMoney(value, fmt, places) {
    const text = String(value ?? "").trim();
    if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(text)) return String(value ?? "");
    const digits = places ?? fmt.decimal_places;
    const negative = text.startsWith("-") && Number(text) !== 0;
    const parts = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        roundingMode: "halfEven",
    }).formatToParts(text.replace(/^[-+]/, ""));
    const number = parts.map((part) => {
        if (part.type === "group") return fmt.thousands_separator;
        if (part.type === "decimal") return fmt.decimal_separator;
        return part.value;
    }).join("");
    const sign = negative ? "-" : "";
    const gap = fmt.symbol_separator;
    if (fmt.symbol_position === "after") return `${sign}${number}${gap}${fmt.currency_symbol}`;
    return `${sign}${fmt.currency_symbol}${gap}${number}`;
}

/**
 * A submitted date ("2026-09-23", as <input type="date"> posts it) in the
 * region's date order, as region_date renders it. Anything else comes back
 * unchanged.
 */
export function formatDate(value, fmt) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""));
    if (!match) return String(value ?? "");
    const [, year, month, day] = match;
    const tokens = { YYYY: year, YY: year.slice(2), MM: month, DD: day };
    return fmt.date_format.replace(/YYYY|YY|MM|DD/g, (token) => tokens[token]);
}
